import React, { useState } from 'react';
import { Card, Input, Textarea, Button } from '../../components/ui';
import { Sparkles, Copy, Download, AlertTriangle, FileText, Settings, ShieldAlert, Users, Scale } from 'lucide-react';
import { cn } from '../../lib/utils';
import { DuesSplitCalculatorTool } from './DuesSplitCalculatorTool';
import { QuorumCheckerTool } from './QuorumCheckerTool';
import { RetentionCalculatorTool } from './RetentionCalculatorTool';
import { BreakEvenCalculatorTool } from './BreakEvenCalculatorTool';
import { RerouteToolComponent } from './RerouteToolComponent';
import { AlumniMentorshipMatcherTool } from './AlumniMentorshipMatcherTool';
import { VolunteerHoursLogTool } from './VolunteerHoursLogTool';
import { PetitionSignatureProjectorTool } from './PetitionSignatureProjectorTool';
import { PhoneBankSchedulerTool } from './PhoneBankSchedulerTool';
import { SponsorshipRoiCalculatorTool } from './SponsorshipRoiCalculatorTool';
import { HoaBudgetTrackerTool } from './HoaBudgetTrackerTool';
import { SportsFeeEstimatorTool } from './SportsFeeEstimatorTool';
import { HoaReserveModelerTool } from './HoaReserveModelerTool';
import { ChurchBudgetEstimatorTool } from './ChurchBudgetEstimatorTool';
import { GardenPlotEstimatorTool } from './GardenPlotEstimatorTool';
import { HoaVendorBidComparerTool } from './HoaVendorBidComparerTool';
import { CommunityLandTrustCalculatorTool } from './CommunityLandTrustCalculatorTool';

import { ImageUpscalerTool } from './ImageUpscalerTool';
import { SilentAuctionBidSheetGenerator } from './SilentAuctionBidSheetGenerator';
import { VolunteerValueCalculator } from './VolunteerValueCalculator';

function ScoutCampBudgetPlannerTool() {
  const [scouts, setScouts] = useState('20');
  const [days, setDays] = useState('3');
  const [nights, setNights] = useState('2');
  
  const [transportRate, setTransportRate] = useState('150');
  const [isTransportPerPerson, setIsTransportPerPerson] = useState(false);
  
  const [foodPerScoutPerDay, setFoodPerScoutPerDay] = useState('15');
  const [campsitePerNight, setCampsitePerNight] = useState('50');
  
  const [bufferPercent, setBufferPercent] = useState('10');

  const numScouts = parseInt(scouts) || 1;
  const numDays = parseInt(days) || 0;
  const numNights = parseInt(nights) || 0;
  
  const tRate = parseFloat(transportRate) || 0;
  const transTotal = isTransportPerPerson ? tRate * numScouts : tRate;
  
  const foodTotal = (parseFloat(foodPerScoutPerDay) || 0) * numScouts * numDays;
  const campTotal = (parseFloat(campsitePerNight) || 0) * numNights;
  
  const subTotal = transTotal + foodTotal + campTotal;
  const bufferNum = parseFloat(bufferPercent) || 0;
  const bufferTotal = subTotal * (bufferNum / 100);
  
  const finalTotal = subTotal + bufferTotal;
  const perScout = finalTotal / Math.max(1, numScouts);

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Trip Parameters</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Total Scouts</label>
              <Input type="number" value={scouts} onChange={e => setScouts(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Days</label>
              <Input type="number" value={days} onChange={e => setDays(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Nights</label>
              <Input type="number" value={nights} onChange={e => setNights(e.target.value)} />
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-4">
          <h3 className="text-lg font-bold">Shared & Individual Costs</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-end mb-1">
                 <label className="text-[10px] uppercase font-bold text-gray-400">Transportation ($)</label>
                 <select 
                   value={isTransportPerPerson ? 'per-person' : 'flat'} 
                   onChange={e => setIsTransportPerPerson(e.target.value === 'per-person')}
                   className="h-7 text-xs border border-gray-200 rounded px-2 bg-white dark:bg-slate-800 dark:border-slate-700 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                 >
                   <option value="flat">Total Amount</option>
                   <option value="per-person">Per Person</option>
                 </select>
              </div>
              <Input type="number" value={transportRate} onChange={e => setTransportRate(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Food/Scout/Day ($)</label>
                <Input type="number" value={foodPerScoutPerDay} onChange={e => setFoodPerScoutPerDay(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400 block mb-1">Site/Night ($)</label>
                <Input type="number" value={campsitePerNight} onChange={e => setCampsitePerNight(e.target.value)} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card className="p-6 bg-slate-50 border-slate-200 dark:bg-slate-900/40 dark:border-slate-800 flex flex-col justify-between h-full">
          <div>
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 block">Budget Breakdown</span>
            <div className="space-y-3">
              <div className="flex justify-between text-sm py-1 border-b border-gray-200/60 dark:border-slate-700/60">
                <span className="text-slate-600 dark:text-slate-400">Transportation:</span>
                <span className="font-mono">${transTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-200/60 dark:border-slate-700/60">
                <span className="text-slate-600 dark:text-slate-400">Food ({days} days):</span>
                <span className="font-mono">${foodTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm py-1 border-b border-gray-200/60 dark:border-slate-700/60">
                <span className="text-slate-600 dark:text-slate-400">Campsite ({nights} nights):</span>
                <span className="font-mono">${campTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm py-2 font-bold">
                <span>Subtotal:</span>
                <span className="font-mono">${subTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700">
               <label className="text-[10px] uppercase font-bold text-slate-500 tracking-wider mb-2 block">Emergency Buffer (%)</label>
               <Input 
                 type="number" 
                 className="bg-white dark:bg-slate-950 w-1/3 mb-4 h-8" 
                 value={bufferPercent} 
                 onChange={e => setBufferPercent(e.target.value)} 
               />
               <div className="flex justify-between text-sm py-1 text-slate-500">
                  <span>Buffer Amount:</span>
                  <span className="font-mono">${bufferTotal.toFixed(2)}</span>
               </div>
            </div>
          </div>

          <div className="mt-8 pt-4 border-t-2 border-slate-300 dark:border-slate-700">
             <div className="flex justify-between items-end">
               <div>
                 <span className="text-lg font-black block text-slate-900 dark:text-white">Suggested Fee</span>
                 <span className="text-xs text-slate-500">Break-even per scout</span>
               </div>
               <div className="text-right">
                 <span className="text-4xl font-black text-blue-600 dark:text-blue-400 font-mono">${Math.ceil(perScout).toFixed(2)}</span>
                 <span className="block text-[10px] uppercase text-slate-400 mt-1 font-mono">Total Budget: ${finalTotal.toFixed(2)}</span>
               </div>
             </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function EventBudgetEstimatorTool({
  input1, setInput1, input2, setInput2, text, setText
}: {
  input1: string, setInput1: (s:string)=>void,
  input2: string, setInput2: (s:string)=>void,
  text: string, setText: (s:string)=>void
}) {
  const [fixedCosts, setFixedCosts] = useState('1000');
  const [attendees, setAttendees] = useState('50');
  const [variableCostPerPerson, setVariableCostPerPerson] = useState('0');
  
  const [batchCost, setBatchCost] = useState('250');
  const [batchSize, setBatchSize] = useState('10'); 
  const [desiredMarginConfig, setDesiredMarginConfig] = useState('20');

  const fixed = parseFloat(fixedCosts) || 0;
  const att = parseInt(attendees) || 1;
  const varPer = parseFloat(variableCostPerPerson) || 0;
  
  const bCost = parseFloat(batchCost) || 0;
  const bSize = parseInt(batchSize) || 1;
  const margin = parseFloat(desiredMarginConfig) || 0;

  const requiredBatches = Math.ceil(att / Math.max(1, bSize));
  const totalBatchCost = requiredBatches * bCost;
  
  const totalCost = fixed + (att * varPer) + totalBatchCost;
  const costPerAttendee = totalCost / Math.max(1, att);
  
  const requiredRevenue = totalCost * (1 + margin / 100);
  const ticketPrice = requiredRevenue / Math.max(1, att);

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-4">
           <h3 className="text-lg font-bold">Standard Costs</h3>
           <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="text-[10px] uppercase font-bold text-gray-400">Fixed Expenses ($)</label>
               <Input type="number" value={fixedCosts} onChange={e => setFixedCosts(e.target.value)} placeholder="Venue, DJ..." />
             </div>
             <div>
               <label className="text-[10px] uppercase font-bold text-gray-400">Cost Per Attendee ($)</label>
               <Input type="number" value={variableCostPerPerson} onChange={e => setVariableCostPerPerson(e.target.value)} placeholder="Swag, Lanyards" />
             </div>
           </div>
           <div>
             <label className="text-[10px] uppercase font-bold text-gray-400">Total Expected Attendees</label>
             <Input type="number" value={attendees} onChange={e => setAttendees(e.target.value)} />
           </div>
        </Card>
        
        <Card className="p-6 space-y-4 border-amber-100 bg-amber-50/50 dark:border-amber-900/50 dark:bg-amber-950/10">
           <div className="flex items-center gap-2">
             <AlertTriangle className="w-5 h-5 text-amber-500" />
             <h3 className="text-lg font-bold">Batched Costs (Complex)</h3>
           </div>
           <p className="text-xs text-gray-500">For variables scaling in groups (e.g., catering platters for 10 people, tables for 8).</p>
           <div className="grid grid-cols-2 gap-4">
             <div>
               <label className="text-[10px] uppercase font-bold text-gray-400">Cost Per Batch ($)</label>
               <Input type="number" value={batchCost} onChange={e => setBatchCost(e.target.value)} />
             </div>
             <div>
               <label className="text-[10px] uppercase font-bold text-gray-400">People Per Batch</label>
               <Input type="number" value={batchSize} onChange={e => setBatchSize(e.target.value)} />
             </div>
           </div>
           {totalBatchCost > 0 && (
             <div className="text-xs bg-white dark:bg-slate-900 border border-gray-200 p-2 rounded text-gray-600 mt-2">
               You will need <strong>{requiredBatches} batches</strong> totaling <strong>${totalBatchCost.toFixed(2)}</strong> to cover {att} attendees.
             </div>
           )}
        </Card>
      </div>

      <div className="space-y-6">
         <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-100/50 border-green-200 dark:from-green-950/20 dark:to-emerald-900/10 dark:border-green-900">
           <span className="text-xs font-bold text-green-700 uppercase block mb-4">Financial Projections</span>
           
           <div className="space-y-3 mb-6">
             <div className="flex justify-between items-center text-sm">
               <span>Total Fixed Costs:</span>
               <span className="font-mono">${fixed.toFixed(2)}</span>
             </div>
             <div className="flex justify-between items-center text-sm">
               <span>Total Variable & Batched:</span>
               <span className="font-mono">${(totalCost - fixed).toFixed(2)}</span>
             </div>
             <div className="flex justify-between items-center text-sm font-bold border-t border-green-200/50 pt-2 mt-2">
               <span>Total Estimated Costs:</span>
               <span className="font-mono">${totalCost.toFixed(2)}</span>
             </div>
             <div className="flex justify-between items-center text-sm mt-1">
               <span className="text-green-800/70 dark:text-green-400/70">Required Break-Even Per Person:</span>
               <span className="font-mono text-green-800/70 dark:text-green-400/70">${costPerAttendee.toFixed(2)}</span>
             </div>
           </div>
           
           <div className="pt-4 border-t border-green-200/50">
             <label className="text-[10px] uppercase font-bold text-green-800 tracking-wider mb-2 block">Desired Profit Margin (%)</label>
             <Input 
               type="number" 
               className="bg-white/60 dark:bg-black/20 border-green-200 mb-6 w-1/3" 
               value={desiredMarginConfig} 
               onChange={e => setDesiredMarginConfig(e.target.value)} 
             />

             <div className="flex justify-between items-end">
               <div>
                 <span className="text-base font-bold block text-green-900 dark:text-green-100">Recommended Ticket Price:</span>
                 <span className="text-xs text-green-700/60 dark:text-green-300">To achieve a {margin}% profit margin</span>
               </div>
               <span className="text-4xl font-black text-green-700 font-mono">${Math.ceil(ticketPrice).toFixed(2)}</span>
             </div>
           </div>
         </Card>
      </div>
    </div>
  );
}

function RobertsRulesTool() {
  const [goal, setGoal] = useState('new');
  const [detail, setDetail] = useState('');

  let recommendation = '';
  let script = '';
  let second = true;
  let debatable = true;
  let amendable = true;
  let vote = 'Majority';

  if (goal === 'new') {
    recommendation = 'Main Motion';
    script = 'I move that [state the action you want to take].';
    second = true;
    debatable = true;
    amendable = true;
    vote = 'Majority';
  } else if (goal === 'modify') {
    recommendation = 'Motion to Amend';
    script = 'I move to amend the motion by [inserting/striking out] the words...';
    second = true;
    debatable = true;
    amendable = true;
    vote = 'Majority';
  } else if (goal === 'delay') {
    if (detail === 'later') {
      recommendation = 'Postpone to a Certain Time';
      script = 'I move to postpone the question to [state specific time or next meeting].';
      second = true;
      debatable = true;
      amendable = true;
      vote = 'Majority';
    } else if (detail === 'committee') {
      recommendation = 'Commit or Refer';
      script = 'I move to refer the question to a committee for further study.';
      second = true;
      debatable = true;
      amendable = true;
      vote = 'Majority';
    } else {
      recommendation = 'Table the Motion (Lay on the Table)';
      script = 'I move to lay the question on the table.';
      second = true;
      debatable = false;
      amendable = false;
      vote = 'Majority';
    }
  } else if (goal === 'stop') {
    recommendation = 'Previous Question (Call the Question)';
    script = 'I move the previous question. (Or "I call the question")';
    second = true;
    debatable = false;
    amendable = false;
    vote = '2/3rds';
  } else if (goal === 'rules') {
    if (detail === 'violation') {
      recommendation = 'Point of Order';
      script = 'Point of order! [Wait for Chair to ask state your point]. The rules are being violated because...';
      second = false;
      debatable = false;
      amendable = false;
      vote = 'Chair Decides';
    } else if (detail === 'info') {
      recommendation = 'Parliamentary Inquiry / Point of Information';
      script = 'Point of information! [Wait for Chair]. I have a question regarding...';
      second = false;
      debatable = false;
      amendable = false;
      vote = 'No Vote';
    } else {
      recommendation = 'Appeal the Decision of the Chair';
      script = 'I appeal the decision of the chair.';
      second = true;
      debatable = true;
      amendable = false;
      vote = 'Majority';
    }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-xl font-bold mb-4 font-display text-slate-900 dark:text-white">What do you want to do?</h3>
            <select 
              value={goal}
              onChange={e => { setGoal(e.target.value); setDetail(''); }}
              className="w-full h-[40px] px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white border-gray-300 text-gray-900 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
            >
              <option value="new">Introduce new business</option>
              <option value="modify">Change the current motion</option>
              <option value="delay">Delay or defer the issue</option>
              <option value="stop">End debate and vote immediately</option>
              <option value="rules">Enforce rules or ask a question</option>
            </select>
          </div>

          {goal === 'delay' && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-2 tracking-widest">How do you want to delay?</label>
              <select 
                value={detail}
                onChange={e => setDetail(e.target.value)}
                className="w-full h-[40px] px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white border-gray-300 text-gray-900 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              >
                <option value="">Set aside temporarily (Table)</option>
                <option value="later">Delay to a specific time</option>
                <option value="committee">Send to a committee to study</option>
              </select>
            </div>
          )}

          {goal === 'rules' && (
            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
              <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-2 tracking-widest">What is the issue?</label>
              <select 
                value={detail}
                onChange={e => setDetail(e.target.value)}
                className="w-full h-[40px] px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white border-gray-300 text-gray-900 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
              >
                <option value="">I disagree with the Chair's ruling</option>
                <option value="violation">A rule is being broken</option>
                <option value="info">I need clarification or information</option>
              </select>
            </div>
          )}
        </Card>
      </div>

      <div className="space-y-6 h-full">
        <Card className="p-6 bg-blue-950/20 border-blue-900/50 h-full flex flex-col justify-center text-center space-y-6">
          <div>
            <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block mb-2">Recommended Motion</span>
            <p className="text-3xl font-black text-blue-100">{recommendation}</p>
          </div>
          
          <div className="bg-slate-900/60 p-4 rounded-xl border border-slate-700 text-left">
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block mb-2">What to Say</span>
            <p className="font-mono text-sm text-blue-300">"{script}"</p>
          </div>

          <div className="grid grid-cols-4 gap-2 text-center pt-4 border-t border-blue-900/50">
            <div>
              <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-1">Second?</span>
              <span className={cn("text-sm font-bold", second ? "text-green-400" : "text-red-400")}>{second ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-1">Debatable?</span>
              <span className={cn("text-sm font-bold", debatable ? "text-green-400" : "text-red-400")}>{debatable ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-1">Amendable?</span>
              <span className={cn("text-sm font-bold", amendable ? "text-green-400" : "text-red-400")}>{amendable ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="block text-[9px] uppercase tracking-wider text-slate-400 mb-1">Vote Req.</span>
              <span className="text-sm font-bold text-amber-400">{vote}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function DocumentGeneratorTool({ slug }: { slug: string }) {
  const [orgName, setOrgName] = useState('My Civic Association');
  const [purpose, setPurpose] = useState('To promote community engagement, volunteer gardening, and neighborhood resilience.');
  const [duesInterval, setDuesInterval] = useState('Annual (Recommended)');
  const [boardSize, setBoardSize] = useState('5');
  const [quorum, setQuorum] = useState('50');
  
  const [waiverActivity, setWaiverActivity] = useState('Community Cleanup Event');
  const [waiverRisks, setWaiverRisks] = useState('Minor cuts, slips, trips, falls, and weather exposure.');

  const [copyStatus, setCopyStatus] = useState(false);

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  let generatedMarkdown = '';
  let title = 'Document Generator';
  let icon = <FileText className="w-5 h-5 text-indigo-500" />;

  if (slug === 'bylaws-templates-builder') {
    title = 'Bylaws & Constitution Builder';
    icon = <Scale className="w-5 h-5 text-indigo-500" />;
    generatedMarkdown = `# Constitution and Bylaws of ${orgName}

## Article I: Name and Purpose
1. **Name**: The official name of this entity shall be **${orgName}**.
2. **Purpose**: The purpose of this organization is ${purpose}

## Article II: Membership & Dues
1. Membership is open to all individuals who support the purpose.
2. Dues shall be collected on a **${duesInterval}** basis.

## Article III: Board of Directors
1. The Board shall consist of **${boardSize}** officers.
2. Quorum for official meetings shall be **${quorum}%** of active voting members.

## Article IV: Amendments
These bylaws may be amended by a two-thirds (2/3) vote at any regular meeting.
`;
  } else if (slug === 'waiver-generator') {
    title = 'Liability Waiver Generator';
    icon = <ShieldAlert className="w-5 h-5 text-red-500" />;
    generatedMarkdown = `# Release of Liability and Assumption of Risk

**Organization**: ${orgName}
**Activity**: ${waiverActivity}

1. **Acknowledgment of Risk**: I acknowledge that participating in ${waiverActivity} involves inherent risks, including but not limited to: ${waiverRisks}.
2. **Release of Liability**: I hereby release and hold harmless ${orgName}, its directors, officers, and volunteers from any liability for injury or property damage arising from my participation.
3. **Medical Consent**: I grant permission for emergency medical treatment if required.

_______________________  Date: _______
Participant Signature
`;
  } else if (slug === 'meeting-minutes-generator') {
    title = 'Meeting Minutes Generator';
    icon = <Users className="w-5 h-5 text-blue-500" />;
    generatedMarkdown = `# Meeting Minutes: ${orgName}

**Date**: [Insert Date]
**Time**: [Insert Time]
**Location**: [Insert Location]

## Attendees
- 
- 

## Call to Order
The meeting was called to order at [Time] by [Name].

## Approval of Minutes
Minutes from the previous meeting were reviewed and approved.

## Old Business
1. 

## New Business
1. 

## Adjournment
Meeting adjourned at [Time].
`;
  } else {
    title = slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    generatedMarkdown = `# ${title}
    
**Organization**: ${orgName}
**Purpose**: ${purpose}

## Section 1: Overview
Provide your standard overview text here.

## Signatures
_______________________  Date: _______
`;
  }

  return (
    <div className="space-y-6 text-left">
      <div className="mb-8">
        <h2 className="text-3xl font-black text-slate-900 dark:text-white font-display flex items-center gap-3">
          {title}
        </h2>
        <p className="text-sm text-slate-500 mt-2">Generate starter markdown-ready organization constitutions, local bylaws, and code of conduct boilerplates.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <Card className="p-6 flex flex-col justify-between">
           <div>
             <div className="flex items-center gap-2 mb-6">
               <FileText className="w-5 h-5 text-indigo-500" />
               <h3 className="text-lg font-bold text-slate-900 dark:text-white">Document Builder</h3>
             </div>
             
             <div className="space-y-5">
               <div>
                 <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Organization Name</label>
                 <Input 
                   value={orgName} 
                   onChange={e => setOrgName(e.target.value)} 
                 />
               </div>

               {slug === 'waiver-generator' ? (
                 <>
                   <div>
                     <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Activity Description</label>
                     <Input value={waiverActivity} onChange={e => setWaiverActivity(e.target.value)} />
                   </div>
                   <div>
                     <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Known Risks</label>
                     <Textarea value={waiverRisks} onChange={e => setWaiverRisks(e.target.value)} className="h-20 resize-none" />
                   </div>
                 </>
               ) : (
                 <>
                   <div>
                     <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Organization Purpose Description</label>
                     <Textarea 
                       value={purpose} 
                       onChange={e => setPurpose(e.target.value)} 
                       className="h-20 resize-none"
                     />
                   </div>
                   
                   {slug === 'bylaws-templates-builder' && (
                     <>
                       <div>
                         <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Dues Payment Interval</label>
                         <select 
                           value={duesInterval} 
                           onChange={e => setDuesInterval(e.target.value)}
                           className="w-full h-[40px] px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white border-gray-300 text-gray-900 dark:bg-slate-900 dark:border-slate-700 dark:text-white"
                         >
                           <option>Annual (Recommended)</option>
                           <option>Monthly</option>
                           <option>Quarterly</option>
                           <option>No Dues</option>
                         </select>
                       </div>
                       <div className="grid grid-cols-2 gap-4">
                         <div>
                           <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Board Size</label>
                           <Input type="number" value={boardSize} onChange={e => setBoardSize(e.target.value)} />
                         </div>
                         <div>
                           <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1 tracking-widest">Quorum (%)</label>
                           <Input type="number" value={quorum} onChange={e => setQuorum(e.target.value)} />
                         </div>
                       </div>
                     </>
                   )}
                 </>
               )}
             </div>
           </div>
        </Card>

        <Card className="p-6 h-full flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <span className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 tracking-widest">Markdown Output</span>
            <Button size="sm" variant="secondary" onClick={() => handleCopy(generatedMarkdown)} className="gap-1 px-3 bg-slate-700 hover:bg-slate-600 text-slate-200 border-none cursor-pointer">
              <Copy size={14} /> {copyStatus ? 'Copied!' : 'Copy Markdown'}
            </Button>
          </div>
          
          <div className="flex-1 bg-slate-900/80 rounded-xl border border-slate-700/50 p-4 font-mono text-xs text-slate-300 overflow-y-auto whitespace-pre-wrap leading-relaxed shadow-inner">
            {generatedMarkdown}
          </div>
          
          <p className="text-[11px] text-slate-500 mt-4 text-center">
            You can drop this Markdown directly into your **Compliance Vault** or **Secure Vault** to preserve your organization structure securely.
          </p>
        </Card>
      </div>
    </div>
  );
}

function MemberAcquisitionCostTool() {
  const [spend, setSpend] = useState('500');
  const [acquired, setAcquired] = useState('20');
  const [avgDues, setAvgDues] = useState('120');
  const [avgTenure, setAvgTenure] = useState('3');

  const s = parseFloat(spend) || 0;
  const a = parseInt(acquired) || 0;
  const d = parseFloat(avgDues) || 0;
  const t = parseFloat(avgTenure) || 0;

  const mac = a > 0 ? s / a : 0;
  const ltv = d * t;
  const ratio = mac > 0 ? ltv / mac : 0;

  let healthColor = 'text-slate-500';
  let healthBg = 'bg-slate-100 dark:bg-slate-800';
  let healthMessage = 'Enter data to see insights.';

  if (a > 0 && mac > 0) {
    if (ratio >= 3) {
      healthColor = 'text-green-500';
      healthBg = 'bg-green-500/10 border-green-500/20';
      healthMessage = 'Excellent! Your LTV is at least 3x your acquisition cost. This is a highly sustainable growth engine. Consider scaling your marketing spend.';
    } else if (ratio >= 1.5) {
      healthColor = 'text-amber-500';
      healthBg = 'bg-amber-500/10 border-amber-500/20';
      healthMessage = 'Fair. You are making more than you spend, but the margin is tight. Look for ways to increase member retention or reduce marketing costs.';
    } else {
      healthColor = 'text-red-500';
      healthBg = 'bg-red-500/10 border-red-500/20';
      healthMessage = 'Warning! Your acquisition cost is dangerously close to or higher than the lifetime value. You are losing money on this campaign.';
    }
  }

  return (
    <div className="grid lg:grid-cols-2 gap-8 text-left">
      <div className="space-y-6">
        <Card className="p-6 space-y-6">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Campaign Performance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1">Total Campaign Cost ($)</label>
                <Input type="number" value={spend} onChange={e => setSpend(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1">New Members Acquired</label>
                <Input type="number" value={acquired} onChange={e => setAcquired(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-slate-200 dark:border-slate-700/50">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4">Member Lifetime Value (LTV)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1">Avg Annual Dues ($)</label>
                <Input type="number" value={avgDues} onChange={e => setAvgDues(e.target.value)} />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500 dark:text-slate-400 block mb-1">Avg Tenure (Years)</label>
                <Input type="number" step="0.5" value={avgTenure} onChange={e => setAvgTenure(e.target.value)} />
              </div>
            </div>
          </div>
        </Card>
      </div>

      <div className="space-y-4 flex flex-col">
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-6 bg-blue-950/20 border-blue-900/50 flex flex-col justify-center items-center text-center">
            <span className="text-[10px] uppercase font-bold text-blue-400 block mb-2 tracking-widest">Cost Per Acquisition (CAC)</span>
            <span className="text-4xl font-black text-blue-100">${mac.toFixed(2)}</span>
          </Card>
          <Card className="p-6 bg-indigo-950/20 border-indigo-900/50 flex flex-col justify-center items-center text-center">
            <span className="text-[10px] uppercase font-bold text-indigo-400 block mb-2 tracking-widest">Lifetime Value (LTV)</span>
            <span className="text-4xl font-black text-indigo-100">${ltv.toFixed(2)}</span>
          </Card>
        </div>

        <Card className={cn("p-6 flex-1 flex flex-col justify-center border", healthBg)}>
          <div className="flex justify-between items-center mb-6">
            <span className="text-sm font-bold uppercase tracking-widest text-slate-300">LTV : CAC Ratio</span>
            <span className={cn("text-5xl font-black font-mono", healthColor)}>
              {ratio.toFixed(1)} <span className="text-2xl text-slate-500">: 1</span>
            </span>
          </div>
          <div className={cn("text-sm p-4 rounded-xl border bg-slate-950/40", healthColor, healthColor.replace('text-', 'border-').replace('500', '500/30'))}>
            {healthMessage}
          </div>
        </Card>
      </div>
    </div>
  );
}

export function GenericToolRenderer({ slug }: { slug: string }) {
  const [input1, setInput1] = useState('1000');
  const [input2, setInput2] = useState('50');
  const [text, setText] = useState('');
  const [copyStatus, setCopyStatus] = useState(false);

  const handleCopy = (content: string) => {
    navigator.clipboard.writeText(content);
    setCopyStatus(true);
    setTimeout(() => setCopyStatus(false), 2000);
  };

  switch (slug) {
    case 'scout-camp-planner':
      return <ScoutCampBudgetPlannerTool />;

    case 'silent-auction-bid-sheet':
      return <SilentAuctionBidSheetGenerator />;

    case 'roberts-rules-motion-finder':
      return <RobertsRulesTool />;

    case 'volunteer-value-calculator':
      return <VolunteerValueCalculator />;

    case 'event-budget-estimator':
      return <EventBudgetEstimatorTool 
        input1={input1} setInput1={setInput1} 
        input2={input2} setInput2={setInput2}
        text={text} setText={setText}
      />;

    case 'meeting-minutes-generator':
    case 'committee-charter-builder':
    case 'conflict-of-interest-policy':
    case 'bylaws-templates-builder':
    case 'waiver-generator':
      return <DocumentGeneratorTool slug={slug} />;

    case 'sponsorship-package-pricer': {
      const targetRev = parseFloat(input1) || 10000;
      return (
        <div className="grid md:grid-cols-3 gap-8 text-left">
          <Card className="p-6 md:col-span-1 space-y-6">
            <div>
              <h3 className="text-lg font-bold mb-1">Target Revenue</h3>
              <p className="text-xs text-gray-500 mb-4">Total amount you need to raise from sponsors.</p>
              <Input type="number" value={input1} onChange={e => setInput1(e.target.value)} placeholder="10000" />
            </div>
            <div className="pt-4 border-t border-gray-100">
               <p className="text-sm font-bold text-gray-700">Pricing Strategy</p>
               <p className="text-xs text-gray-500 mt-2">This model assumes 1 Title sponsor covering 40% of the goal, 2 Gold covering 30%, 4 Silver covering 20%, and 10 Bronze covering 10%.</p>
            </div>
          </Card>
          <div className="md:col-span-2 space-y-4">
            <Card className="p-4 flex justify-between items-center bg-indigo-50 border-indigo-100 dark:bg-indigo-950/20 dark:border-indigo-900">
              <div>
                <span className="text-[10px] font-bold text-indigo-600 tracking-widest uppercase">Title Sponsor (Limit: 1)</span>
                <p className="text-2xl font-black mt-1">${(targetRev * 0.40).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
              </div>
              <div className="text-right text-xs text-indigo-700/70 dark:text-indigo-300">
                Premium Branding<br/>Speaking Opportunity
              </div>
            </Card>
            <Card className="p-4 flex justify-between items-center bg-amber-50 border-amber-100 dark:bg-amber-950/20 dark:border-amber-900">
              <div>
                <span className="text-[10px] font-bold text-amber-600 tracking-widest uppercase">Gold Sponsor (Limit: 2)</span>
                <p className="text-2xl font-black mt-1">${(targetRev * 0.15).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal text-amber-700/50">each</span></p>
              </div>
              <div className="text-right text-xs text-amber-700/70 dark:text-amber-300">
                Logo on Signage<br/>Vendor Booth
              </div>
            </Card>
            <Card className="p-4 flex justify-between items-center bg-slate-100 border-slate-200 dark:bg-slate-800 dark:border-slate-700">
              <div>
                <span className="text-[10px] font-bold text-slate-600 dark:text-slate-400 tracking-widest uppercase">Silver Sponsor (Limit: 4)</span>
                <p className="text-2xl font-black mt-1">${(targetRev * 0.05).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal text-slate-500">each</span></p>
              </div>
              <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                Logo on Website<br/>Event Mentions
              </div>
            </Card>
            <Card className="p-4 flex justify-between items-center bg-orange-50 border-orange-100 dark:bg-orange-950/20 dark:border-orange-900">
              <div>
                <span className="text-[10px] font-bold text-orange-700 tracking-widest uppercase">Bronze Sponsor (Limit: 10)</span>
                <p className="text-2xl font-black mt-1">${(targetRev * 0.01).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal text-orange-700/50">each</span></p>
              </div>
              <div className="text-right text-xs text-orange-700/70 dark:text-orange-300">
                Program Listing<br/>Social Shoutout
              </div>
            </Card>
          </div>
        </div>
      );
    }
    case 'fundraising-tier-calculator': {
      const goal = parseFloat(input1) || 50000;
      return (
        <div className="grid md:grid-cols-2 gap-8 text-left">
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-bold">Campaign Goal</h3>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Total Goal ($)</label>
              <Input type="number" value={input1} onChange={e => setInput1(e.target.value)} placeholder="50000" />
            </div>
            <p className="text-xs text-gray-500">Standard Gift Table assumes 1 lead gift (10-20% of goal), a few major gifts, and broad-base support.</p>
          </Card>
          <div className="space-y-3">
             <Card className="p-4 border-l-4 border-l-blue-500">
                <span className="text-xs font-bold text-gray-500">Lead Gift (1 Prospect Needed)</span>
                <p className="text-xl font-bold">${(goal * 0.15).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
             </Card>
             <Card className="p-4 border-l-4 border-l-indigo-400">
                <span className="text-xs font-bold text-gray-500">Major Gifts (4 Prospects Needed)</span>
                <p className="text-xl font-bold">${(goal * 0.05).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal">each</span></p>
             </Card>
             <Card className="p-4 border-l-4 border-l-teal-400">
                <span className="text-xs font-bold text-gray-500">Special Gifts (10 Prospects Needed)</span>
                <p className="text-xl font-bold">${(goal * 0.02).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-sm font-normal">each</span></p>
             </Card>
             <Card className="p-4 border-l-4 border-l-gray-300">
                <span className="text-xs font-bold text-gray-500">Base Support (Multiple)</span>
                <p className="text-xl font-bold">Remainder <span className="text-sm font-normal">(${ (goal * 0.45).toLocaleString(undefined, { maximumFractionDigits:0 }) } total)</span></p>
             </Card>
          </div>
        </div>
      );
    }
    case 'member-acquisition-cost':
      return <MemberAcquisitionCostTool />;
    case 'dues-split-calculator':
    case 'chapter-dues-splitter':
      return <DuesSplitCalculatorTool />;

    case 'quorum-and-roll-checker':
    case 'ceu-tracker':
      return <QuorumCheckerTool />;

    case 'member-retention-estimator':
    case 'member-retention-calculator':
      return <RetentionCalculatorTool />;

    case 'ticket-breakeven-calculator':
    case 'pta-fundraiser-planner':
    case 'sports-tournament-budget':
    case 'gala-catering-calculator':
      return <BreakEvenCalculatorTool />;

    case 'voting-weight-calculator': {
      const chapter = parseInt(input1) || 150;
      const totalMembers = parseInt(input2) || 5000;
      const totalDelegates = parseInt(text) || 100;
      const weight = totalMembers > 0 ? (chapter / totalMembers) * totalDelegates : 0;
      
      return (
        <div className="grid md:grid-cols-2 gap-8 text-left">
          <Card className="p-6 space-y-4">
            <h3 className="text-lg font-bold">Delegation Setup</h3>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Total National/Regional Members</label>
              <Input type="number" value={input2} onChange={e => setInput2(e.target.value)} />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400">Total Delegates Available</label>
              <Input type="number" value={text || '100'} onChange={e => setText(e.target.value)} />
            </div>
            <div className="pt-4 border-t border-gray-100">
              <label className="text-[10px] uppercase font-bold text-gray-400">Your Chapter's Member Count</label>
              <Input type="number" value={input1} onChange={e => setInput1(e.target.value)} />
            </div>
          </Card>
          <Card className="p-6 flex flex-col justify-center items-center text-center bg-violet-50 border-violet-100 dark:bg-violet-900/20 dark:border-violet-800">
             <span className="text-sm font-bold text-violet-600 uppercase mb-2">Your Allocated Delegates</span>
             <span className="text-6xl font-black text-violet-700 dark:text-violet-400">{Math.round(weight)}</span>
             <p className="text-xs text-violet-600/70 mt-4 max-w-xs">Based on proportional representation ({(totalMembers > 0 ? (chapter/totalMembers)*100 : 0).toFixed(1)}% of total membership). Fractional delegates rounded to nearest whole person.</p>
          </Card>
        </div>
      );
    }

    case 'alumni-mentorship-match':
      return <AlumniMentorshipMatcherTool />;

    case 'volunteer-hours-log':
      return <VolunteerHoursLogTool />;

    case 'petition-signature-projector':
      return <PetitionSignatureProjectorTool />;

    case 'phone-bank-scheduler':
      return <PhoneBankSchedulerTool />;

    case 'sponsorship-roi-calculator':
      return <SponsorshipRoiCalculatorTool />;

    case 'hoa-budget-tracker':
      return <HoaBudgetTrackerTool />;

    case 'sports-fee-estimator':
      return <SportsFeeEstimatorTool />;

    case 'hoa-reserve-modeler':
      return <HoaReserveModelerTool />;

    case 'church-budget-estimator':
      return <ChurchBudgetEstimatorTool />;

    case 'garden-bed-estimator':
      return <GardenPlotEstimatorTool />;

    case 'hoa-bid-comparer':
      return <HoaVendorBidComparerTool />;

    case 'image-upscaler':
      return <ImageUpscalerTool />;

    case 'community-land-trust-calculator':
      return <CommunityLandTrustCalculatorTool />;

    default:
      return <RerouteToolComponent fallbackName={slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />;
  }
}
